import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

/* Marketing/public landing page. Style adapted from
 * chatbot.travelminds.com — same design tokens, same section rhythm. */

const features = [
  {
    title: 'Branded email builder',
    desc: 'Drag-and-drop layout with per-agency brand assets — logos, fonts, colors, social links. Build a campaign in minutes that looks like it came from the agency itself.',
  },
  {
    title: 'Live segmentation',
    desc: 'Slice contacts by tags, prior bookings, engagement, and custom fields. Segments stay fresh as data flows in from your CRM and reservation systems.',
  },
  {
    title: 'Deliverability built in',
    desc: 'Bounce and complaint tracking on every send. Sub-domain authentication, DKIM, SPF, DMARC — set once per agency, enforced on every campaign.',
  },
  {
    title: 'Real analytics, not vanity',
    desc: 'Open rate, click-through, bounce rate, complaint rate, unsubscribe — by campaign and over time. Drill into individual messages to see what landed.',
  },
  {
    title: 'Marketing calendar',
    desc: 'One view of everything scheduled across every agency. Move sends, avoid overlaps, plan a quarter ahead without juggling spreadsheets.',
  },
  {
    title: 'Multi-tenant from day one',
    desc: 'Each agency has its own brand, sender identity, contacts, and analytics. Admins switch between agencies; advisors only see their own.',
  },
];

const stats = [
  { value: 'Multi-tenant', label: 'one team, many agencies, one bill' },
  { value: 'White-label', label: 'every campaign goes out under your brand' },
  { value: '24/7', label: 'always-on sending and analytics' },
  { value: 'Daily', label: 'bounce + complaint reconciliation' },
];

const audience = [
  'Independent travel advisors',
  'Travel agency networks',
  'River + ocean cruise specialists',
  'Tour and FIT operators',
  'Destination wedding planners',
  'Luxury travel concierges',
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate]);

  return (
    <>
      {/* Hero */}
      <section className="text-center px-4 sm:px-6 py-16 sm:py-24 max-w-5xl mx-auto">
        <h1
          className="text-5xl sm:text-6xl md:text-7xl font-bold text-[var(--text)] leading-[1.05] tracking-tight mb-6"
          style={{ fontFamily: "'Quicksand', sans-serif" }}
        >
          Email marketing<br />for travel agencies.
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[var(--text-muted)] mb-10 max-w-3xl mx-auto leading-relaxed">
          Branded email under every advisor's name. Live segmentation against
          your contact list. Real-time deliverability so the right inboxes
          actually get your campaigns.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/signup"
            className="bg-[var(--primary)] text-white px-7 py-3.5 rounded-lg font-semibold text-base hover:bg-[var(--primary-light)] transition-colors shadow-sm"
          >
            Register
          </Link>
          <Link
            to="/demo"
            className="bg-white border border-[var(--border)] text-[var(--text)] px-7 py-3.5 rounded-lg font-semibold text-base hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
          >
            View demo
          </Link>
          <Link
            to="/pricing"
            className="bg-white border border-[var(--border)] text-[var(--text)] px-7 py-3.5 rounded-lg font-semibold text-base hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
          >
            View pricing
          </Link>
        </div>
      </section>

      {/* Stats band */}
      <section className="px-4 sm:px-6 py-12 bg-[var(--primary)]">
        <div className="max-w-5xl mx-auto grid gap-6 grid-cols-2 md:grid-cols-4">
          {stats.map((p) => (
            <div key={p.label} className="text-center">
              <div className="text-3xl font-bold text-white mb-1">{p.value}</div>
              <p className="text-sm text-white/70 leading-relaxed">{p.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl font-bold text-[var(--text)] text-center mb-3"
            style={{ fontFamily: "'Quicksand', sans-serif" }}
          >
            Built for agencies that run real campaigns
          </h2>
          <p className="text-[var(--text-muted)] text-center mb-12 max-w-2xl mx-auto">
            Not a generic ESP with a travel skin. Every feature is shaped by
            how multi-agency operators actually work — many brands, one team,
            tight deliverability.
          </p>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg border border-[var(--border)] p-6">
                <h3 className="font-semibold text-[var(--text)] mb-1.5">{f.title}</h3>
                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-[var(--bg)]">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-bold text-[var(--text)] text-center mb-3"
            style={{ fontFamily: "'Quicksand', sans-serif" }}
          >
            From sign-up to first send, fast
          </h2>
          <p className="text-[var(--text-muted)] text-center mb-12 max-w-2xl mx-auto">
            Most agencies have their first campaign out the same week they start.
            DNS and sender authentication are usually the long pole.
          </p>
          <div className="grid gap-8 grid-cols-2 md:grid-cols-4">
            {[
              { step: '1', title: 'Onboard', desc: 'Import contacts, add brand assets, point your DNS records.' },
              { step: '2', title: 'Authenticate', desc: 'DKIM, SPF, DMARC verified per sending domain — once.' },
              { step: '3', title: 'Build', desc: 'Drag-and-drop a layout, write the copy, pick the segment.' },
              { step: '4', title: 'Send + measure', desc: 'Live opens, clicks, bounces, complaints by campaign and over time.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-10 h-10 rounded-full bg-[var(--primary)] text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-semibold text-[var(--text)] mb-2">{s.title}</h3>
                <p className="text-sm text-[var(--text-muted)]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl font-bold text-[var(--text)] mb-3"
            style={{ fontFamily: "'Quicksand', sans-serif" }}
          >
            Who we work with
          </h2>
          <p className="text-[var(--text-muted)] mb-8">
            Travel businesses with a contact list, a brand, and a need to send
            more campaigns without growing the team.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {audience.map((u) => (
              <span
                key={u}
                className="bg-white border border-[var(--border)] rounded-full px-4 py-2 text-sm text-[var(--text)]"
              >
                {u}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 py-16 sm:py-24 text-center bg-[var(--bg)]">
        <h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--text)] mb-5 leading-tight"
          style={{ fontFamily: "'Quicksand', sans-serif" }}
        >
          Ready to send your next campaign?
        </h2>
        <p className="text-lg text-[var(--text-muted)] mb-10 max-w-xl mx-auto">
          Register in minutes. Bring your own domain, your own brand, your own
          list. We handle the deliverability so you can focus on the copy.
        </p>
        <Link
          to="/signup"
          className="bg-[var(--primary)] text-white px-8 py-3.5 rounded-lg font-semibold text-base hover:bg-[var(--primary-light)] transition-colors inline-block shadow-sm"
        >
          Register
        </Link>
      </section>
    </>
  );
};

export default Landing;
